import { consoleApiSlice } from "./consoleApiSlice";

function appendCallerParams(params, caller) {
  if (!caller) return;
  const callers = Array.isArray(caller) ? caller : [caller];
  callers.filter(Boolean).forEach((c) => params.append("caller", c));
}

export const consoleCostAnalyticsApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCostSummary: builder.query({
      query: ({ startDate, endDate, env, websiteId, scanId, caller, purpose, scanMode } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        if (websiteId) params.append("website_id", websiteId);
        if (scanId) params.append("scan_id", scanId);
        appendCallerParams(params, caller);
        if (purpose) params.append("purpose", purpose);
        if (scanMode) params.append("scan_mode", scanMode);
        return { url: `/cost-analytics/summary?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostSummary"],
    }),

    getCostMonthlySummary: builder.query({
      query: ({ startDate, endDate, env, websiteId, caller, purpose, scanMode } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        if (websiteId) params.append("website_id", websiteId);
        if (caller) params.append("caller", caller);
        if (purpose) params.append("purpose", purpose);
        if (scanMode) params.append("scan_mode", scanMode);
        const qs = params.toString();
        return { url: qs ? `/cost-analytics/monthly-summary?${qs}` : "/cost-analytics/monthly-summary", method: "GET" };
      },
      providesTags: ["CostMonthlySummary"],
    }),

    getCostTrends: builder.query({
      query: ({ startDate, endDate, env, groupBy = "purpose", websiteId, caller, purpose, scanMode, topN } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        params.append("group_by", groupBy);
        if (websiteId) params.append("website_id", websiteId);
        appendCallerParams(params, caller);
        if (purpose) params.append("purpose", purpose);
        if (scanMode) params.append("scan_mode", scanMode);
        if (topN) params.append("top_n", topN.toString());
        return { url: `/cost-analytics/trends?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostTrends"],
    }),

    getCostBreakdown: builder.query({
      query: ({ startDate, endDate, env, groupBy = "purpose", websiteId, caller, purpose, scanMode } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        params.append("group_by", groupBy);
        if (websiteId) params.append("website_id", websiteId);
        appendCallerParams(params, caller);
        if (purpose) params.append("purpose", purpose);
        if (scanMode) params.append("scan_mode", scanMode);
        return { url: `/cost-analytics/breakdown?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostBreakdown"],
    }),

    getCostTopScans: builder.query({
      query: ({ startDate, endDate, env, caller, purpose, scanMode, websiteId, limit = 20, sortBy = "cost" } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        appendCallerParams(params, caller);
        if (purpose) params.append("purpose", purpose);
        if (scanMode) params.append("scan_mode", scanMode);
        if (websiteId) params.append("website_id", websiteId);
        params.append("limit", limit.toString());
        params.append("sort_by", sortBy);
        return { url: `/cost-analytics/top-scans?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostTopScans"],
    }),

    getCostFilterOptions: builder.query({
      query: ({ startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        return { url: `/cost-analytics/filter-options?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostFilterOptions"],
    }),

    getCostScanEfficiency: builder.query({
      query: ({ startDate, endDate, env, caller } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        appendCallerParams(params, caller);
        return { url: `/cost-analytics/scan-efficiency?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostScanEfficiency"],
    }),

    getCostStepDistribution: builder.query({
      query: ({ startDate, endDate, env, caller } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (env) params.append("env", env);
        appendCallerParams(params, caller);
        return { url: `/cost-analytics/step-distribution?${params.toString()}`, method: "GET" };
      },
      providesTags: ["CostStepDistribution"],
    }),

    getScanMetadata: builder.query({
      query: ({ env, scanId }) => ({
        url: `/cost-analytics/scan-metadata/${env}/${scanId}`,
        method: "GET",
      }),
      providesTags: (result, error, { scanId }) => [{ type: "ScanMetadata", id: scanId }],
    }),

    getScanPurposeBreakdown: builder.query({
      query: ({ scanId, startDate, endDate }) => {
        const params = new URLSearchParams();
        params.append("scan_id", scanId);
        // Scope to the page's date range so scans older than the default
        // 30-day window still show their breakdown.
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        return { url: `/cost-analytics/summary?${params.toString()}`, method: "GET" };
      },
      providesTags: (result, error, { scanId }) => [{ type: "ScanPurposeBreakdown", id: scanId }],
    }),
  }),
});

export const {
  useGetCostSummaryQuery,
  useGetCostMonthlySummaryQuery,
  useGetCostTrendsQuery,
  useGetCostBreakdownQuery,
  useGetCostTopScansQuery,
  useGetCostFilterOptionsQuery,
  useGetCostScanEfficiencyQuery,
  useGetCostStepDistributionQuery,
  useGetScanMetadataQuery,
  useGetScanPurposeBreakdownQuery,
} = consoleCostAnalyticsApiSlice;

// Backwards-compat alias for the renamed query — keeps existing imports working.
export const useGetScanTagBreakdownQuery = useGetScanPurposeBreakdownQuery;
